import { keyframes } from "@emotion/react";
import { Box, Image, Theme, Typography } from "@outschool/backpack";
import {
  HOMEPAGE_PREVIEW_IMAGE,
  imageUrl as getImageUrl
} from "@outschool/filestack-urls";
import {
  SUPPORTED_LOCALES,
  Trans,
  useTranslation
} from "@outschool/localization";
import { onboardingQuizPath, pathToUrl } from "@outschool/routes";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  TrustpilotMicroCombo,
  TrustpilotStarter,
  getWidgetUrlPrefix
} from "@outschool/ui-components-website";
import { Loading, TrackingPage } from "@outschool/ui-legacy-component-library";
import { lazyWithRetry, useIsMobile, useNavigation } from "@outschool/ui-utils";
import React from "react";
import { Helmet } from "react-helmet";

import HeroArchitect from "../../images/homepage/home_hero/homepage_occupations/architect.webp";
import HeroArtist from "../../images/homepage/home_hero/homepage_occupations/artist.webp";
import HeroAstronaut from "../../images/homepage/home_hero/homepage_occupations/astronaut.webp";
import HeroChef from "../../images/homepage/home_hero/homepage_occupations/chef.webp";
import HeroDoctor from "../../images/homepage/home_hero/homepage_occupations/doctor.webp";
import HeroEngineer from "../../images/homepage/home_hero/homepage_occupations/engineer.webp";
import HeroExecutive from "../../images/homepage/home_hero/homepage_occupations/executive.webp";
import HeroGraduate from "../../images/homepage/home_hero/homepage_occupations/graduate.webp";
import HeroMusician from "../../images/homepage/home_hero/homepage_occupations/musician.webp";
import HeroScientist from "../../images/homepage/home_hero/homepage_occupations/scientist.webp";
import { MAIN_IMAGE_HEIGHT, MAIN_IMAGE_WIDTH } from "../../shared/Activity";
import { APP_HOSTNAME, OUTSCHOOL_ENV } from "../../shared/Env";
import SimpleLayout from "../components/layouts/SimpleLayout";

const BelowTheFoldLoggedOutUserHome = lazyWithRetry(
  /* webpackChunkName: "BelowTheFoldLoggedOutUserHome" */
  () => import("../components/BelowTheFoldLoggedOutUserHome")
);

function TrustpilotWidget() {
  const isMobile = useIsMobile();
  return isMobile ? (
    <TrustpilotStarter
      sx={(theme: Theme) => ({
        width: "125%",
        transform: "scale(0.8)",
        textAlign: "left",
        [theme.breakpoints.down("sm")]: {
          marginLeft: "-80px"
        }
      })}
      urlPrefix={getWidgetUrlPrefix({
        isSSR: true,
        env: OUTSCHOOL_ENV,
        appHostname: APP_HOSTNAME!
      })}
    />
  ) : (
    <TrustpilotMicroCombo
      urlPrefix={getWidgetUrlPrefix({
        isSSR: true,
        env: OUTSCHOOL_ENV,
        appHostname: APP_HOSTNAME!
      })}
    />
  );
}

function RotatingKids({
  kidImageUrls,
  pauseSeconds,
  transitionSeconds
}: {
  kidImageUrls: string[];
  pauseSeconds: number;
  transitionSeconds: number;
}) {
  const timePerImage = pauseSeconds + transitionSeconds;
  const totalDuration = kidImageUrls.length * timePerImage;

  const toPercent = (time: number) => (time / totalDuration) * 100;
  const fadeOutStart = toPercent(pauseSeconds);
  const fadeOutEnd = toPercent(pauseSeconds + transitionSeconds);
  const fadeInStart = 100 - toPercent(transitionSeconds);

  const swap = keyframes`
                  0% { opacity: 1; }
    ${fadeOutStart}% { opacity: 1; } 
    ${fadeOutEnd}%   { opacity: 0; }
    ${fadeInStart}%  { opacity: 0; }
                100% { opacity: 1; }`;

  return (
    <Box
      sx={(theme: Theme) => ({
        flexShrink: 0,
        width: "470px",
        height: "478px",
        position: "relative",
        zIndex: 1,
        overflow: "hidden",
        display: "inline-flex",
        alignItems: "center",
        gridArea: "image",
        marginTop: "5rem",
        [theme.breakpoints.down("md")]: {
          marginTop: "0",
          width: "300px",
          height: "308px"
        }
      })}
    >
      {[...new Set(kidImageUrls)].map((url, idx) => (
        <Image
          alt={`image of kid ${idx}`}
          key={url}
          src={url}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            animation: `${swap} ${totalDuration}s ease-in-out infinite`,
            animationDelay: `${idx * timePerImage - totalDuration}s`,
            opacity: 0 // Set initial opacity to 0 to prevent all images showing at once
          }}
          sizes={`(max-width: 768px) 80vw, 50vw`}
        />
      ))}
    </Box>
  );
}

function LoggedOutUserHero() {
  const { t } = useTranslation("client\\components\\NewUserHome");

  const heroKidImages = [
    HeroArchitect,
    HeroAstronaut,
    HeroArtist,
    HeroChef,
    HeroDoctor,
    HeroEngineer,
    HeroExecutive,
    HeroGraduate,
    HeroMusician,
    HeroScientist
  ];
  const navigation = useNavigation();
  return (
    <Box
      sx={{
        position: "relative",
        backgroundSize: "100% 275px",
        backgroundRepeat: "no-repeat"
      }}
    >
      <Box
        sx={(theme: Theme) => ({
          marginX: "auto",
          maxWidth: "1120px",
          [theme.breakpoints.up("lg")]: {
            marginBottom: "11rem"
          }
        })}
      >
        <Box
          flex
          sx={(theme: Theme) => ({
            flexDirection: "column-reverse",
            alignItems: "center",
            paddingTop: "3.3rem",
            [theme.breakpoints.up("md")]: {
              flexDirection: "row",
              justifyContent: "space-around",
              alignItems: "start",
              paddingBottom: "4.8rem"
            },
            [theme.breakpoints.up("lg")]: {
              paddingTop: "5.6rem",
              paddingBottom: "1.4rem",
              justifyContent: "space-between"
            }
          })}
        >
          <Box
            sx={(theme: Theme) => ({
              display: "inline-flex",
              flexDirection: "column",
              gap: "1em",
              alignItems: "center",
              width: "100%",
              textAlign: "center",
              [theme.breakpoints.down("sm")]: {
                maxWidth: "406px",
                textAlign: "left",
                alignItems: "start",
                paddingX: "1em"
              },
              [theme.breakpoints.up("sm")]: {
                width: "406px"
              },
              [theme.breakpoints.up("md")]: {
                width: "406px",
                textAlign: "left",
                alignItems: "start"
              },
              [theme.breakpoints.up("lg")]: {
                alignItems: "start",
                width: "640px",
                textAlign: "left",
                paddingTop: "5.4rem",
                marginTop: theme.spacing(19)
              }
            })}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column"
              }}
            >
              <Typography
                emphasized={true}
                sx={{
                  position: "relative",
                  marginBottom: "3.2rem",
                  textAlign: "left",
                  typography: "display1"
                }}
              >
                <Trans t={t}>Custom Learning.</Trans>
              </Typography>
              <Typography
                sx={(theme: Theme) => ({
                  color: theme.palette.primary.main,
                  typography: "display1"
                })}
              >
                <Trans t={t}>Lifelong Impact.</Trans>
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{ fontSize: "1.8rem", maxWidth: "550px" }}
            >
              <Trans t={t}>
                Live online small-group classes and 1:1 tutoring led by real
                teachers, designed to spark curiosity and build real-world
                skills.
              </Trans>
            </Typography>
            <TrackedButton
              trackingName="onboarding-quiz-get-started-button"
              variant="contained"
              sx={(theme: Theme) => ({
                marginTop: "1.5em",
                marginBottom: "2em",
                paddingY: "1.3rem",
                [theme.breakpoints.up("md")]: {
                  alignSelf: "start"
                }
              })}
              onClick={() =>
                navigation(onboardingQuizPath(), { hardNav: true })
              }
            >
              <Typography variant="body1" sx={{ fontSize: "1.8rem" }}>
                {t("Get Started")}
              </Typography>
            </TrackedButton>
            <Box sx={{ width: "100%", overflow: "hidden" }}>
              <TrustpilotWidget />
            </Box>
          </Box>
          <RotatingKids
            kidImageUrls={heroKidImages}
            pauseSeconds={3.3}
            transitionSeconds={0.3}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default function LoggedOutUserHome({ ...props }) {
  const [triggerLoaded, setTriggerLoaded] = React.useState(false);
  React.useEffect(() => {
    setTriggerLoaded(true);
  }, []);

  return (
    <TrackingPage
      impressionEventName={"Home Page Impression"}
      uniqueId="homePage"
      name="homePage"
      useLinkTracking
    >
      {setImpressionNode => (
        <SimpleLayout
          ref={node => {
            setImpressionNode(node!);
          }}
          {...props}
          allowCategoriesNavbar
        >
          <Box>
            <LoggedOutUserHero />
            {triggerLoaded ? (
              <React.Suspense fallback={<Loading />}>
                <BelowTheFoldLoggedOutUserHome />
              </React.Suspense>
            ) : null}
          </Box>
        </SimpleLayout>
      )}
    </TrackingPage>
  );
}

export function HomePageMetaTags() {
  const { t } = useTranslation("client\\components\\NewUserHome");
  const canonicalUrl = pathToUrl("/");
  const title = t(
    "Get affordable online classes and tutoring for kids at Outschool"
  );
  const description = t(
    "Find the perfect academic resource for your kids. Try online classes and tutoring from Outschool’s vetted 5-star teachers. No contracts or sales calls"
  );
  let hrefLangUrls = [];
  for (let supportedLocale of SUPPORTED_LOCALES) {
    let langUrl = pathToUrl("/", supportedLocale);
    // @ts-ignore
    hrefLangUrls.push({
      rel: "alternate",
      hreflang: supportedLocale,
      href: langUrl
    });
  }

  // both dimensions must be > 200 for Facebook to accept as
  // link preview
  const previewImageWidth = 2 * MAIN_IMAGE_WIDTH;
  const previewImageHeight = 2 * MAIN_IMAGE_HEIGHT;

  const imageUrl = getImageUrl(HOMEPAGE_PREVIEW_IMAGE, {
    w: previewImageWidth,
    h: previewImageHeight,
    fit: "clip"
  });

  return (
    <Helmet>
      <title>{title}</title>
      <meta property="og:title" content={title} />
      <meta name="description" content={description.slice(0, 155)} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      {hrefLangUrls.map((link, index) => (
        <link
          key={index}
          rel={link.rel}
          hrefLang={link.hreflang}
          href={link.href}
        />
      ))}
    </Helmet>
  );
}
